import { supabase } from "../../../lib/supabase";
import type { BookingState } from "../../../contexts/BookingWizardContext";

export const ensureDraftBooking = async (
  companyId: number | null,
  existingId?: number
): Promise<number> => {
  if (existingId) return existingId;
  if (!companyId) {
    throw new Error("Company is required to create a booking");
  }

  const { data, error } = await supabase
    .from("bookings")
    .insert({
      company_id: companyId,
      status: "concept",
    })
    .select("id")
    .single();

  if (error || !data) {
    throw error ?? new Error("Failed to create draft booking");
  }

  return data.id;
};

export const markV2Booking = (bookingId: number): void => {
  try {
    sessionStorage.setItem(`booking-wizard-v2:${bookingId}`, "1");
  } catch {
    // ignore storage errors
  }
};

export const isV2Booking = (bookingId: number): boolean => {
  try {
    return sessionStorage.getItem(`booking-wizard-v2:${bookingId}`) === "1";
  } catch {
    return false;
  }
};

export const buildInitialV2State = (
  data: Record<string, unknown>,
  currentStep: number
): BookingState => {
  const deliveryDetails = data.delivery_details
    ? Array.isArray(data.delivery_details)
      ? data.delivery_details[0]
      : data.delivery_details
    : undefined;

  const deliveryType: "storage" | "address" | undefined =
    deliveryDetails != null ? "address" : "storage";

  return {
    id: data.id as number,
    status: (data.status as string) ?? "concept",
    reference: data.reference as string | undefined,
    rejection_reason: data.rejection_reason as string | null | undefined,
    transport_type: (data.transport_type as { id: number } | null)?.id,
    booking_type: (data.booking_type as { id: number } | null)?.id,
    booking_incoterm: (data.booking_incoterm as { id: number } | null)?.id,
    supplier_id: (data.supplier as { id: number } | null)?.id,
    products: data.products as BookingState["products"],
    commercial_invoice_file: data.commercial_invoice_file as string | undefined,
    origin_pickup_date: data.origin_pickup_date as string | undefined,
    company_id: data.company_id as number,
    currentStep,
    isComplete: false,
    delivery_type: deliveryType,
    storage_option: (data.storage_option as BookingState["storage_option"]) ?? undefined,
    service_level: (data.service_level as BookingState["service_level"]) ?? undefined,
    delivery_details: deliveryDetails
      ? {
          id: deliveryDetails.id,
          country: deliveryDetails.country,
          city: deliveryDetails.city,
          postal_code: deliveryDetails.postal_code,
          street_name: deliveryDetails.street_name,
          house_number: deliveryDetails.house_number,
          instructions: deliveryDetails.instructions,
        }
      : undefined,
  };
};
